import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Smile, Image as ImageIcon, Video, Mic, Square, X, Loader2, Pencil, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import MentionPicker from "@/components/MentionPicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const QUICK_EMOJIS = ["😀","😂","🥰","😍","😎","🤔","😅","😭","😡","🥳","❤️","🔥","👍","👏","🙏","🎉","💯","✨","🚀","💪","😴","🤝","👀","🤣","😊","😉","😜","😇","🤗","🫶"];
const MAX_VIDEO_MB = 50;
const MAX_IMAGE_MB = 10;

async function uploadBlob(blob, name, onProgress) {
  const fd = new FormData();
  fd.append("file", blob, name);
  const r = await axios.post(`${API}/upload`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: (e) => { if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100)); },
  });
  return r.data.url;
}

const fmtMB = (bytes) => `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

/** Preview a picked photo/video before sending; shows upload progress. */
function MediaPreviewDialog({ media, progress, sending, onCancel, onConfirm }) {
  const [caption, setCaption] = useState("");
  useEffect(() => { setCaption(""); }, [media]);
  if (!media) return null;
  return (
    <Dialog open={!!media} onOpenChange={(v) => { if (!v && !sending) onCancel(); }}>
      <DialogContent className="max-w-md" data-testid="chat-media-preview">
        <DialogHeader><DialogTitle>{media.type === "video" ? "Enviar vídeo" : "Enviar foto"}</DialogTitle></DialogHeader>
        <div className="rounded-xl overflow-hidden bg-black flex items-center justify-center max-h-[50vh]">
          {media.type === "video"
            ? <video src={media.url} controls playsInline className="max-h-[50vh] w-full" data-testid="chat-media-preview-video" />
            : <img src={media.url} alt="" className="max-h-[50vh] object-contain" data-testid="chat-media-preview-image" />}
        </div>
        <div className="text-xs text-muted-foreground flex justify-between">
          <span className="truncate max-w-[70%]">{media.file.name}</span>
          <span>{fmtMB(media.file.size)}</span>
        </div>
        <Input value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Legenda (opcional)" data-testid="chat-media-caption" disabled={sending}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onConfirm(caption); } }} />
        {sending && (
          <div className="space-y-1" data-testid="chat-media-progress">
            <div className="h-2 rounded-full bg-muted overflow-hidden"><div className="h-full bg-gradient-to-r from-teal-500 to-sky-600 transition-[width]" style={{ width: `${progress}%` }} /></div>
            <div className="text-[11px] text-muted-foreground text-right">{progress < 100 ? `Enviando... ${progress}%` : "Processando..."}</div>
          </div>
        )}
        <div className="flex gap-2 justify-end">
          <Button variant="outline" className="rounded-full" onClick={onCancel} disabled={sending} data-testid="chat-media-cancel">Cancelar</Button>
          <Button className="rounded-full bg-gradient-to-br from-teal-500 via-cyan-500 to-sky-600 text-white border-0" onClick={() => onConfirm(caption)} disabled={sending} data-testid="chat-media-send">
            {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />} Enviar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Composer({ conversationId, replyTo, onCancelReply, onSend, editing, onCancelEdit, onSaveEdit, draft }) {
  const [text, setText] = useState("");
  const [picked, setPicked] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [media, setMedia] = useState(null); // { file, url, type }
  const [recording, setRecording] = useState(false);
  const [recSeconds, setRecSeconds] = useState(0);
  const inputRef = useRef(null);
  const imgRef = useRef(null);
  const vidRef = useRef(null);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const lastTypingRef = useRef(0);
  const recSecondsRef = useRef(0);

  useEffect(() => { setText(""); setMedia(null); }, [conversationId]);
  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);
  // Enter edit mode: prefill the input with the message text
  useEffect(() => {
    if (editing) { setText(editing.content || ""); setTimeout(() => inputRef.current?.focus(), 30); }
  }, [editing]);
  // "Recortar": drop text into the input for re-sending/editing
  useEffect(() => {
    if (draft?.text) { setText(draft.text); setTimeout(() => inputRef.current?.focus(), 30); }
  }, [draft]);
  useEffect(() => () => { if (media?.url) URL.revokeObjectURL(media.url); }, [media]);

  const ping = () => {
    const now = Date.now();
    if (now - lastTypingRef.current > 2000) {
      lastTypingRef.current = now;
      axios.post(`${API}/chat/conversations/${conversationId}/typing`).catch(() => {});
    }
  };

  const sendText = async () => {
    const content = text.trim();
    if (!content) return;
    if (editing) {
      if (content === (editing.content || "").trim()) { onCancelEdit?.(); setText(""); return; }
      setText("");
      await onSaveEdit?.(editing, content);
      inputRef.current?.focus();
      return;
    }
    const mention_ids = picked.filter((u) => content.toLowerCase().includes(`@${(u.name || "").split(" ")[0].toLowerCase()}`)).map((u) => u.user_id);
    setText(""); setPicked([]);
    await onSend({ type: "text", content, mention_ids });
    inputRef.current?.focus();
  };

  const pickFile = (file, type) => {
    if (!file) return;
    const limit = type === "video" ? MAX_VIDEO_MB : MAX_IMAGE_MB;
    if (file.size > limit * 1024 * 1024) { toast.error(`Arquivo muito grande (máx. ${limit}MB)`); return; }
    setMedia({ file, type, url: URL.createObjectURL(file) });
  };

  const confirmMedia = async (caption) => {
    if (!media) return;
    setUploading(true); setProgress(0);
    try {
      const url = await uploadBlob(media.file, media.file.name, setProgress);
      await onSend({ type: media.type, media_url: url, content: (caption || "").trim() });
      setMedia(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Falha no upload"); }
    setUploading(false);
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" : "audio/webm";
      const rec = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        const secs = recSecondsRef.current;
        if (blob.size < 1000 || secs < 1) { toast.info("Áudio muito curto"); return; }
        setUploading(true);
        try {
          const url = await uploadBlob(blob, `voice-${Date.now()}.webm`);
          await onSend({ type: "audio", media_url: url, content: "", duration: secs });
        } catch (e) { toast.error(e.response?.data?.detail || "Falha ao enviar áudio"); }
        setUploading(false);
      };
      rec.start(250);
      recRef.current = rec;
      setRecording(true);
      setRecSeconds(0); recSecondsRef.current = 0;
      timerRef.current = setInterval(() => { recSecondsRef.current += 1; setRecSeconds(s => s + 1); }, 1000);
    } catch { toast.error("Permita o microfone para gravar áudio"); }
  };

  const stopRecording = (cancel = false) => {
    if (timerRef.current) clearInterval(timerRef.current);
    setRecording(false);
    const rec = recRef.current;
    if (!rec) return;
    if (cancel) { rec.ondataavailable = null; rec.onstop = () => rec.stream.getTracks().forEach(t => t.stop()); }
    rec.stop();
    recRef.current = null;
  };

  const sendBtnClass = "rounded-full shrink-0 bg-gradient-to-br from-teal-500 via-cyan-500 to-sky-600 hover:from-teal-600 hover:via-cyan-600 hover:to-sky-700 text-white shadow-md shadow-cyan-500/30 border-0";

  return (
    <div className="border-t border-border shrink-0" data-testid="chat-composer">
      {editing && (
        <div className="flex items-center gap-2 px-3 pt-2" data-testid="composer-edit-banner">
          <div className="flex-1 text-xs rounded-lg px-2 py-1 bg-muted border-l-2 border-amber-500">
            <div className="font-semibold flex items-center gap-1"><Pencil className="w-3 h-3" /> Editando mensagem</div>
            <div className="truncate opacity-80">{editing.content}</div>
          </div>
          <Button size="icon" variant="ghost" className="rounded-full w-7 h-7" onClick={() => { setText(""); onCancelEdit?.(); }} data-testid="composer-edit-cancel" aria-label="Cancelar edição"><X className="w-4 h-4" /></Button>
        </div>
      )}
      {replyTo && !editing && (
        <div className="flex items-center gap-2 px-3 pt-2" data-testid="composer-reply-banner">
          <div className="flex-1 text-xs rounded-lg px-2 py-1 bg-muted border-l-2 border-primary">
            <div className="font-semibold">Respondendo a {replyTo.from_name}</div>
            <div className="truncate opacity-80">{replyTo.type === "text" ? replyTo.content : ({ image: "📷 Foto", video: "🎥 Vídeo", audio: "🎤 Áudio" }[replyTo.type])}</div>
          </div>
          <Button size="icon" variant="ghost" className="rounded-full w-7 h-7" onClick={onCancelReply} data-testid="composer-reply-cancel"><X className="w-4 h-4" /></Button>
        </div>
      )}
      <div className="p-2 md:p-3 flex gap-1.5 items-center">
        {recording ? (
          <div className="flex-1 flex items-center gap-3 px-3" data-testid="composer-recording">
            <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
            <span className="text-sm font-mono">{String(Math.floor(recSeconds / 60)).padStart(2, "0")}:{String(recSeconds % 60).padStart(2, "0")}</span>
            <span className="text-xs text-muted-foreground flex-1">Gravando áudio...</span>
            <Button size="sm" variant="ghost" onClick={() => stopRecording(true)} data-testid="composer-record-cancel">Cancelar</Button>
            <Button size="icon" className="rounded-full bg-emerald-600 hover:bg-emerald-700" onClick={() => stopRecording(false)} data-testid="composer-record-stop" aria-label="Enviar áudio"><Square className="w-4 h-4" /></Button>
          </div>
        ) : (
          <>
            <Popover>
              <PopoverTrigger asChild><Button variant="ghost" size="icon" data-testid="chat-emoji-btn" className="rounded-full text-muted-foreground shrink-0"><Smile className="w-5 h-5" /></Button></PopoverTrigger>
              <PopoverContent className="w-64 p-2" data-testid="chat-emoji-picker">
                <div className="grid grid-cols-6 gap-1">{QUICK_EMOJIS.map(e => <button key={e} onClick={() => { setText(t => t + e); inputRef.current?.focus(); }} data-testid={`emoji-${e}`} className="text-xl hover:bg-muted rounded p-1">{e}</button>)}</div>
              </PopoverContent>
            </Popover>
            <input ref={imgRef} type="file" accept="image/*" className="hidden" data-testid="chat-image-input" onChange={(e) => { pickFile(e.target.files?.[0], "image"); e.target.value = ""; }} />
            <input ref={vidRef} type="file" accept="video/*" className="hidden" data-testid="chat-video-input" onChange={(e) => { pickFile(e.target.files?.[0], "video"); e.target.value = ""; }} />
            {!editing && <Button variant="ghost" size="icon" className="rounded-full text-muted-foreground shrink-0" data-testid="chat-image-btn" onClick={() => imgRef.current?.click()} disabled={uploading} aria-label="Enviar foto"><ImageIcon className="w-5 h-5" /></Button>}
            {!editing && <Button variant="ghost" size="icon" className="rounded-full text-muted-foreground shrink-0" data-testid="chat-video-btn" onClick={() => vidRef.current?.click()} disabled={uploading} aria-label="Enviar vídeo"><Video className="w-5 h-5" /></Button>}
            <div className="relative flex-1 min-w-0">
              <Input ref={inputRef} data-testid="chat-input" placeholder={editing ? "Edite sua mensagem" : "Mensagem"} value={text} className="rounded-full"
                onChange={(e) => { setText(e.target.value); if (!editing) ping(); }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.defaultPrevented) { e.preventDefault(); sendText(); }
                  if (e.key === "Escape" && editing) { setText(""); onCancelEdit?.(); }
                }} />
              <MentionPicker value={text} onChange={setText} inputRef={inputRef} onPick={(u) => setPicked((p) => (p.some((x) => x.user_id === u.user_id) ? p : [...p, u]))} />
            </div>
            {uploading ? (
              <Button size="icon" className="rounded-full shrink-0" disabled><Loader2 className="w-4 h-4 animate-spin" /></Button>
            ) : editing ? (
              <Button data-testid="chat-edit-save" onClick={sendText} size="icon" className={`${sendBtnClass} !from-amber-500 !via-orange-500 !to-amber-600`} disabled={!text.trim()} aria-label="Salvar edição"><Check className="w-4 h-4" /></Button>
            ) : text.trim() ? (
              <Button data-testid="chat-send" onClick={sendText} size="icon" className={sendBtnClass} aria-label="Enviar"><Send className="w-4 h-4" /></Button>
            ) : (
              <Button data-testid="chat-record-btn" onClick={startRecording} size="icon" className={sendBtnClass} aria-label="Gravar áudio"><Mic className="w-4 h-4" /></Button>
            )}
          </>
        )}
      </div>
      <MediaPreviewDialog media={media} progress={progress} sending={uploading} onCancel={() => setMedia(null)} onConfirm={confirmMedia} />
    </div>
  );
}
